import { type Immutable } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { getActiveDayPart } from '../layout/getHorizonWindow';
import { type ServiceDisplayWidget } from '../layout/types';
import { useServiceDisplayDayParts } from './useServiceDisplayDayParts';

const TICK_MS = 60_000;

/**
 * Returns the label of the day part currently active for this widget, or `null` when
 * none is active. Only considers day parts the widget is associated with via a
 * day-part-specific threshold override. Re-evaluates every minute so the label appears
 * and disappears on day-part boundaries without a page refresh.
 */
export function useActiveDayPartLabel(widget: Immutable<ServiceDisplayWidget>): string | null {
  const [tick, setTick] = useState(0);
  const { data: dayParts = [] } = useServiceDisplayDayParts();
  const hasThresholds = (widget.thresholds?.length ?? 0) > 0;

  useEffect(() => {
    if (!hasThresholds) return;
    const interval = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(interval);
  }, [hasThresholds]);

  return useMemo(() => {
    // `tick` is in the dep list so we re-evaluate every minute.
    void tick;
    if (!hasThresholds) return null;
    const now = new Date();
    const dp = getActiveDayPart({
      now: now.getTime(),
      localNow: now,
      widgetThresholds: widget.thresholds ?? [],
      allDayParts: dayParts,
    });
    return dp?.label ?? null;
  }, [hasThresholds, widget.thresholds, dayParts, tick]);
}
